import type { INormalizedResponse } from '@/interfaces/api';
import NovaClient from '@/clients/nova/NovaClient';
import type {
  INovaAppointment,
  INovaCarrier,
  INovaCompany,
  INovaCreatedAppointment,
  INovaDock,
  INovaLoadType,
  INovaLoggedInUser,
  INovaLoginAsResult,
  INovaOrg,
  INovaPersistedWarehouse,
  INovaUser,
  INovaWarehouse
} from '@/interfaces/nova';
import {
  mapAppointment,
  mapDock,
  mapLoadType,
  mapWarehouse,
  type NovaAppointmentResponse,
  type NovaDockResponse,
  type NovaLoadTypeResponse,
  type NovaWarehouseResponse
} from '@/services/nova/NovaMap';

export type ConstructorParams = {
  token: string | null;
};

export class NovaApiService {
  readonly client;
  constructor({ token }: ConstructorParams) {
    this.client = new NovaClient({ token });
  }

  async getOrgs(params = {}): Promise<INormalizedResponse<INovaOrg>> {
    return await this.client.get<INovaOrg>('/org', params);
  }

  async getUsers(params = {}): Promise<INormalizedResponse<INovaUser>> {
    return await this.client.get<INovaUser>('/user', params);
  }

  async getCompanies(params = {}): Promise<INormalizedResponse<INovaCompany>> {
    return await this.client.get<INovaCompany>('/company', params);
  }

  async getLoadTypes(params = {}): Promise<INormalizedResponse<INovaLoadType>> {
    const response = await this.client.get<NovaLoadTypeResponse>('/loadtype', params);
    return { ...response, data: response.data.map(loadType => mapLoadType(loadType)) };
  }

  async getWarehouses(params = {}): Promise<INormalizedResponse<INovaPersistedWarehouse>> {
    const response = await this.client.get<NovaWarehouseResponse>('/warehouse', params);
    return { ...response, data: response.data.map(warehouse => mapWarehouse(warehouse)) };
  }

  async getDocks(params = {}): Promise<INormalizedResponse<INovaDock>> {
    const response = await this.client.get<NovaDockResponse>('/dock', params);
    return { ...response, data: response.data.map(dock => mapDock(dock)) };
  }

  async getAppointments(params = {}): Promise<INormalizedResponse<INovaAppointment>> {
    const response = await this.client.get<NovaAppointmentResponse>('/appointment', params);
    return { ...response, data: response.data.map(appointment => mapAppointment(appointment)) };
  }

  async getLoggedInAsUser(): Promise<INormalizedResponse<INovaLoggedInUser>> {
    return await this.client.get<INovaLoggedInUser>('auth/me');
  }

  async loginAs(userId: string): Promise<INormalizedResponse<INovaLoginAsResult>> {
    return await this.client.post<INovaLoginAsResult>(`wormhole/login-as/${userId}`);
  }

  async createWarehouse(warehouse: INovaWarehouse): Promise<INormalizedResponse<INovaWarehouse>> {
    return await this.client.post<INovaWarehouse>('/warehouse', warehouse);
  }

  async createLoadType(loadType: INovaLoadType): Promise<INormalizedResponse<INovaLoadType>> {
    return await this.client.post<INovaLoadType>('/loadtype', loadType);
  }

  async createDock(dock: INovaDock): Promise<INormalizedResponse<INovaDock>> {
    return this.client.post<INovaDock>('/dock', dock);
  }

  async createCompany(companyName: string): Promise<INormalizedResponse<INovaCompany>> {
    return this.client.post<INovaCompany>('/company', { name: companyName });
  }

  async getCarriers(params = {}): Promise<INormalizedResponse<INovaCarrier>> {
    return await this.client.get<INovaCarrier>('/carrier', params);
  }

  async createCarrier(carrierData: object): Promise<INormalizedResponse<INovaCarrier>> {
    return this.client.post<INovaCarrier>('/carrier', carrierData);
  }

  async createAppointment(params = {}): Promise<INormalizedResponse<INovaCreatedAppointment>> {
    return await this.client.post<INovaCreatedAppointment>('/appointment', params);
  }

  async createReserve(params = {}): Promise<INormalizedResponse<INovaCreatedAppointment>> {
    return await this.client.post<INovaCreatedAppointment>('/appointment/reserve', params);
  }

  async updateOrg(orgId: string, params = {}): Promise<INormalizedResponse<INovaOrg>> {
    return await this.client.patch<INovaOrg>(`/org/${orgId}`, params);
  }

  async updateDock(dockId: string, params = {}) {
    return await this.client.patch(`/dock/${dockId}`, params);
  }

  async updateWarehouse(warehouseId: string, params = {}) {
    return await this.client.patch(`/warehouse/${warehouseId}`, params);
  }

  async inviteUser(params = {}) {
    return await this.client.post(`/user/invite`, params);
  }
}
